import { Phone, MessageCircle, Mail, Clock, MapPin, Instagram, Facebook, Youtube, ChevronDown } from "lucide-react";
import { Reveal, PageHero, CTAButton } from "@/components/bits";
import { EnquiryForm } from "@/components/Forms";
import { BRAND, BRANCHES, FAQS, waLink } from "@/data/site";

const Contact = () => (
  <>
    <PageHero
      eyebrow="Contact Us"
      title={<>Talk to the <span className="text-red-500">academy</span></>}
      sub="Call, WhatsApp, email or visit either of our two branches — whichever works best for you."
    />

    <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="contact-page">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Phone, label: "Call Us", value: BRAND.phoneDisplay, href: BRAND.phoneLink, id: "phone" },
          { icon: MessageCircle, label: "WhatsApp", value: BRAND.whatsappDisplay, href: waLink(), id: "whatsapp" },
          { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, id: "email" },
          { icon: Clock, label: "Business Hours", value: BRAND.hours, href: null, id: "hours" },
        ].map((c, i) => (
          <Reveal key={c.id} delay={i * 0.07}>
            {c.href ? (
              <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="card-glow block h-full border border-white/10 bg-[#0F0F14] p-7" data-testid={`contact-${c.id}`}>
                <c.icon size={22} className="text-red-500" aria-hidden="true" />
                <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">{c.label}</p>
                <p className="mt-2 break-words text-sm font-semibold text-white">{c.value}</p>
              </a>
            ) : (
              <div className="card-glow h-full border border-white/10 bg-[#0F0F14] p-7" data-testid={`contact-${c.id}`}>
                <c.icon size={22} className="text-red-500" aria-hidden="true" />
                <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">{c.label}</p>
                <p className="mt-2 text-sm font-semibold text-white">{c.value}</p>
              </div>
            )}
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-5 md:grid-cols-2">
        {BRANCHES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.1}>
            <article className="card-glow border border-white/10 bg-[#0F0F14] p-8 sm:p-10" data-testid={`contact-branch-${c.id}`}>
              <span className="font-mono2 text-[10px] tracking-[0.3em] text-red-600">{c.label}</span>
              <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight text-white">{c.name}</h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.address}</p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">
                Open {BRAND.hours}. Tap below for turn-by-turn directions on Google Maps.
              </p>
              <div className="mt-7">
                <CTAButton href={c.maps} testid={`contact-directions-${c.id}`}>
                  <MapPin size={15} /> Get Directions
                </CTAButton>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-10 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">Send us a message</h2>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            Fill in the form and our team will get in touch with you shortly.
          </p>
          <div className="mt-8 border border-white/10 bg-[#0F0F14] p-6 sm:p-8">
            <EnquiryForm compact />
          </div>
        </Reveal>

        <div>
          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">Common questions</h2>
            <div className="mt-8 space-y-3" data-testid="faq-list">
              {FAQS.map((f, i) => (
                <details key={i} className="group border border-white/10 bg-[#0F0F14]" data-testid={`faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 text-sm font-semibold text-white transition-colors hover:text-red-400 [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <ChevronDown size={16} className="shrink-0 text-red-500 transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <p className="px-6 pb-6 text-[13px] leading-relaxed text-zinc-400">{f.a}</p>
                </details>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <h2 className="mt-12 font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">Follow the journey</h2>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: Instagram, name: "@anilraj0072", href: BRAND.instagram1, id: "ig1" },
                { icon: Instagram, name: "@newboogiewoogieacademy1", href: BRAND.instagram2, id: "ig2" },
                { icon: Facebook, name: "Facebook", href: BRAND.facebook, id: "fb" },
                { icon: Youtube, name: "YouTube", href: BRAND.youtube, id: "yt" },
              ].map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" className="card-glow flex items-center gap-3 border border-white/10 bg-[#0F0F14] px-5 py-4" data-testid={`contact-social-${s.id}`}>
                  <s.icon size={17} className="shrink-0 text-red-500" aria-hidden="true" />
                  <span className="truncate text-[13px] font-semibold text-white">{s.name}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);

export default Contact;
